import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/* A first-visit walkthrough: the page dims and one part of it at a time is lit
   up, with a line on what it's for. Shown once per member on this device
   (replay from the ? in the menu). A step whose target isn't on screen — the
   menu collapses on a phone — is shown as a centred card instead. */

const STEPS = [
  { title: "Welcome to Builders' Network 👋",
    text: 'A private network for people building in Web3, AI and fintech. Here are the few things worth knowing — it takes 30 seconds.' },
  { target: '.app-main', title: 'Home',
    text: "Everyone's posts, newest first. The colour shows what a post is: Ask (amber), Test (violet), Offer (green)." },
  { target: '.app-rail', title: 'Find it again',
    text: 'Search everything (press /), and use My activity — My posts, I commented, Mentions me, Saved — to get back to a conversation. Your open requests are here too.' },
  { target: '[data-tour="compose"]', title: 'Post, or ask for help',
    text: 'Share an update, or pick Ask, Test, Offer, Intro, Looking For or Refer when you need something. Requests stay on your Open Requests until you mark them solved.' },
  { target: '[data-tour="nav-people"]', title: 'People',
    text: "Your connections and private chats, introductions from B/N, and Discover to find members and ask to connect — you're connected when you both say yes. Invite the builders you rate from here too." },
  { target: '[data-tour="nav-groups"]', title: 'Groups',
    text: 'Private working groups with your connections — topics, pinned links and files, @mentions. Start one for a project, a pilot or a deal.' },
  { target: '[data-tour="nav-copilot"]', title: 'Copilot',
    text: 'Your AI assistant. Ask anything, paste a link to an article, or attach a document for a summary, watch-outs or a mind map.' },
  { target: '[data-tour="nav-profile"]', title: 'My profile',
    text: 'Under your name: fill in "What the matcher uses" — what you need and what you offer. It is the single biggest thing that makes your introductions good.' },
  { title: "You're set",
    text: 'Say hello in a post, or reply to one. Replay this tour anytime from your name at the bottom of the menu.' },
];

const PAD = 8;
const CARD_W = 320;

function visibleRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return null;
  return r;
}

function cardPosition(r) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - 32);
  if (!r) return { left: (vw - w) / 2, top: Math.max(16, vh / 2 - 110), width: w };
  const clampX = (x) => Math.max(16, Math.min(x, vw - w - 16));
  const clampY = (y) => Math.max(16, Math.min(y, vh - 240));
  if (vw - r.right > w + 32) return { left: r.right + 16, top: clampY(r.top), width: w };       // right of it
  if (r.left > w + 32) return { left: r.left - w - 16, top: clampY(r.top), width: w };           // left of it
  if (vh - r.bottom > 220) return { left: clampX(r.left), top: r.bottom + 16, width: w };        // below
  return { left: clampX(r.left), top: Math.max(16, r.top - 236), width: w };                    // above
}

export default function Tour({ onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[i];

  const measure = useCallback(() => setRect(visibleRect(step.target)), [step.target]);
  useLayoutEffect(() => {
    const el = step.target && document.querySelector(step.target);
    if (el && el.scrollIntoView && step.target !== '.app-main') el.scrollIntoView({ block: 'nearest' });
    measure();
  }, [measure, step.target]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, STEPS.length - 1));
      if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [measure, onClose]);

  const last = i === STEPS.length - 1;
  const pos = cardPosition(rect);
  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="Builders' Network tour">
      {rect
        ? <div className="tour-hole" style={{ left: rect.left - PAD, top: rect.top - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2 }} />
        : <div className="tour-dim" />}
      <div className="tour-card" style={pos}>
        <p className="tour-count">{i + 1} of {STEPS.length}</p>
        <h3>{step.title}</h3>
        <p>{step.text}</p>
        <div className="tour-actions">
          {i > 0 && <button className="btn quiet sm" onClick={() => setI(i - 1)}>Back</button>}
          <button className="btn primary sm" autoFocus onClick={() => (last ? onClose() : setI(i + 1))}>
            {last ? 'Done' : i === 0 ? 'Show me' : 'Next'}
          </button>
          {!last && <button className="linklike tour-skip" onClick={onClose}>Skip</button>}
        </div>
      </div>
    </div>
  );
}

const KEY = (id) => `bn.tour.v1.${id}`;
export function tourSeen(memberId) {
  try { return Boolean(window.localStorage.getItem(KEY(memberId))); } catch { return true; }
}
export function markTourSeen(memberId) {
  try { window.localStorage.setItem(KEY(memberId), '1'); } catch { /* private mode */ }
}
